import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const PublicMarketplaceContext = createContext();

export const usePublicMarketplace = () => {
  const context = useContext(PublicMarketplaceContext);
  if (!context) {
    throw new Error('usePublicMarketplace must be used within PublicMarketplaceProvider');
  }
  return context;
};

export const PublicMarketplaceProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [profile, setProfile] = useState(null);
  const [cart, setCart] = useState([]);
  const [phoneNumber, setPhoneNumber] = useState(null);
  const [tenantSlug, setTenantSlug] = useState(null);
  const [tenantInfo, setTenantInfo] = useState(null);
  const [tenantLoading, setTenantLoading] = useState(false);

  // Cargar información del tenant
  const loadTenantInfo = useCallback(async (slug) => {
    // Cargar si el slug es diferente O si no hay tenantInfo cargado
    if (!slug) return;
    if (slug === tenantSlug && tenantInfo) return;
    
    setTenantLoading(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${API_URL}/api/public/marketplace/info/${slug}`);
      
      if (response.ok) {
        const data = await response.json();
        setTenantInfo(data);
        setTenantSlug(slug);
        console.log('🏪 [TENANT] Información cargada:', data.name);
      } else {
        console.error('❌ [TENANT] Marketplace no encontrado:', slug);
        setTenantInfo(null);
      }
    } catch (error) {
      console.error('❌ [TENANT] Error cargando tenant:', error);
    } finally {
      setTenantLoading(false);
    }
  }, [tenantSlug, tenantInfo]);

  // Load from localStorage on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('marketplace_token');
    const storedProfile = localStorage.getItem('marketplace_profile');
    const storedCart = localStorage.getItem('marketplace_cart');
    const storedPhone = localStorage.getItem('marketplace_phone');

    // La sesión permanece guardada hasta que el usuario cierre sesión.
    if (storedToken) {
      setToken(storedToken);
      if (storedProfile) setProfile(JSON.parse(storedProfile));
      if (storedCart) setCart(JSON.parse(storedCart));
      if (storedPhone) setPhoneNumber(storedPhone);
    }
  }, []);

  const login = (newToken, newProfile, phone) => {
    setToken(newToken);
    setProfile(newProfile);
    setPhoneNumber(phone);
    localStorage.setItem('marketplace_token', newToken);
    localStorage.setItem('marketplace_profile', JSON.stringify(newProfile));
    localStorage.setItem('marketplace_phone', phone);
  };

  const logout = () => {
    setToken(null);
    setProfile(null);
    setPhoneNumber(null);
    setCart([]);
    localStorage.removeItem('marketplace_token');
    localStorage.removeItem('marketplace_profile');
    localStorage.removeItem('marketplace_phone');
    localStorage.removeItem('marketplace_cart');
  };

  const updateProfile = (updatedProfile) => {
    console.log('🔄 [CONTEXT] Actualizando perfil:', updatedProfile);
    setProfile(updatedProfile);
    localStorage.setItem('marketplace_profile', JSON.stringify(updatedProfile));
  };

  const refreshProfile = async () => {
    if (!token) return;
    
    try {
      const { getMyProfile } = await import('../services/publicMarketplaceService');
      const updatedProfile = await getMyProfile(token);
      updateProfile(updatedProfile);
      console.log('✅ [CONTEXT] Perfil recargado desde servidor:', updatedProfile.total_points, 'puntos');
      return updatedProfile;
    } catch (error) {
      console.error('❌ [CONTEXT] Error recargando perfil:', error);
      throw error;
    }
  };

  const addToCart = (product, variant = null, quantity = 1) => {
    const newCart = [...cart];
    const existingIndex = newCart.findIndex(
      item => item.product.id === product.id && 
               (variant ? item.variant?.id === variant.id : !item.variant)
    );

    if (existingIndex >= 0) {
      newCart[existingIndex].quantity += quantity;
    } else {
      newCart.push({
        product,
        variant,
        quantity,
        points: variant 
          ? product.points_cost + variant.additional_points 
          : product.points_cost
      });
    }

    setCart(newCart);
    localStorage.setItem('marketplace_cart', JSON.stringify(newCart));
  };

  const removeFromCart = (productId, variantId = null) => {
    const newCart = cart.filter(
      item => !(item.product.id === productId && 
                (variantId ? item.variant?.id === variantId : !item.variant))
    );
    setCart(newCart);
    localStorage.setItem('marketplace_cart', JSON.stringify(newCart));
  };

  const updateCartItemQuantity = (productId, variantId, quantity) => {
    const newCart = cart.map(item => {
      if (item.product.id === productId && 
          (variantId ? item.variant?.id === variantId : !item.variant)) {
        return { ...item, quantity };
      }
      return item;
    });
    setCart(newCart);
    localStorage.setItem('marketplace_cart', JSON.stringify(newCart));
  };

  const clearCart = () => {
    setCart([]);
    localStorage.removeItem('marketplace_cart');
  };

  const getTotalPoints = () => {
    return cart.reduce((total, item) => total + (item.points * item.quantity), 0);
  };

  const getTotalItems = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  const value = {
    token,
    profile,
    phoneNumber,
    cart,
    login,
    logout,
    updateProfile,
    refreshProfile,
    addToCart,
    removeFromCart,
    updateCartItemQuantity,
    clearCart,
    getTotalPoints,
    getTotalItems,
    isAuthenticated: !!token,
    isProfileComplete: profile?.is_profile_complete || false,
    // Multitenant
    tenantSlug,
    tenantInfo,
    tenantLoading,
    loadTenantInfo,
    setTenantSlug
  };

  return (
    <PublicMarketplaceContext.Provider value={value}>
      {children}
    </PublicMarketplaceContext.Provider>
  );
};
